import type { useQueryClient } from '@tanstack/react-query';

import { propertyQueryKeys } from '~/hooks/apis/accommodation/useGetProperty';

import type { OfferBuilderProperty } from '../PropertyInformation/types';

import type { MediaSourceImage } from './types';

// Bumped on every direct cache write, so a stale in-flight hydration fetch can detect and skip itself.
const roomTypeImageWriteVersion = new Map<string, number>();

function roomTypeCacheKey(propertyId: string, roomTypeId: string) {
  return `${propertyId}:${roomTypeId}`;
}

export function getRoomTypeImageWriteVersion(propertyId: string, roomTypeId: string) {
  return roomTypeImageWriteVersion.get(roomTypeCacheKey(propertyId, roomTypeId)) ?? 0;
}

function bumpRoomTypeImageWriteVersion(propertyId: string, roomTypeId: string) {
  const key = roomTypeCacheKey(propertyId, roomTypeId);
  roomTypeImageWriteVersion.set(key, (roomTypeImageWriteVersion.get(key) ?? 0) + 1);
}

export function patchPropertyRoomTypeImagesInCache(
  queryClient: ReturnType<typeof useQueryClient>,
  propertyId: string,
  roomTypeId: string,
  images: Array<MediaSourceImage>,
) {
  bumpRoomTypeImageWriteVersion(propertyId, roomTypeId);
  queryClient.setQueryData(propertyQueryKeys.detail(propertyId), (oldProperty: OfferBuilderProperty | undefined) => {
    if (!oldProperty?.room_types) {
      return oldProperty;
    }

    return {
      ...oldProperty,
      room_types: oldProperty.room_types.map((roomType) =>
        roomType.id === roomTypeId ? { ...roomType, images } : roomType,
      ),
    };
  });
}

export function removePropertyRoomTypeImageFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  propertyId: string,
  roomTypeId: string,
  publicId: string,
) {
  bumpRoomTypeImageWriteVersion(propertyId, roomTypeId);
  queryClient.setQueryData(propertyQueryKeys.detail(propertyId), (oldProperty: OfferBuilderProperty | undefined) => {
    if (!oldProperty?.room_types) {
      return oldProperty;
    }

    return {
      ...oldProperty,
      room_types: oldProperty.room_types.map((roomType) =>
        roomType.id === roomTypeId
          ? {
              ...roomType,
              images: (roomType.images ?? []).filter((image) => image.id_cloudinary_external !== publicId),
            }
          : roomType,
      ),
    };
  });
}
